import { useState, useMemo } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import FileUpload from '@cloudscape-design/components/file-upload';
import Tabs from '@cloudscape-design/components/tabs';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import { parseEML, extractTextFromHTML, type ParsedEmail } from '@utils/emlMsgParser';
import { parseEmailHeaders, type ParsedEmailHeaders } from '@utils/emailHeaderParser';
import { analyzeBulkInvisibleCharacters, type InvisibleCharBulkAnalysisResult } from '@utils/invisibleCharacterAnalyzer';
import { analyzeURL } from '@utils/urlSafetyChecker';
import { decodeSafeLinksUrl, isSafeLinksUrl } from '@utils/safeLinkDecoder';
import { InvisibleCharacterAnalysis } from '@components/InvisibleCharacterAnalysis';
import { logger } from '@utils/logger';
import { detectInvisibleChars } from '@utils/invisibleCharacters';
import { analyzeSecurityRisks } from '@utils/securityAnalysis';
import { AnalysisCache } from '@utils/debounce';

// Global cache instance for analysis results
const emailAnalysisCache = new AnalysisCache();

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const MailEmlAnalyzerTab = () => {
  const [selectedFile, setSelectedFile] = useState<File[]>([]);
  const [parseError, setParseError] = useState('');
  const [parsedEmail, setParsedEmail] = useState<ParsedEmail | null>(null);
  const [bodyTabId, setBodyTabId] = useState('text');
  const [highlightedPosition, setHighlightedPosition] = useState<number | null>(null);


  const handleFileChange = ({ detail }: { detail: { value: File[] } }) => {
    const files = detail.value;
    setSelectedFile(files);
    setParseError('');
    setParsedEmail(null);

    if (files.length === 0) return;

    const file = files[0];

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setParseError(`File size exceeds maximum limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // Validate file extension
    const fileExt = file.name.toLowerCase().split('.').pop();
    if (fileExt !== 'eml') {
      setParseError('Only .eml files are supported');
      return;
    }

    // Parse the file
    parseEMLFile(file);
  };

  const parseEMLFile = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseEML(content);
        setParsedEmail(parsed);
        logger.info('EML file parsed successfully');
      } catch (error) {
        logger.error('Failed to parse EML file', error);
        setParseError(`Failed to parse email file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    reader.onerror = () => {
      setParseError('Failed to read file');
    };

    reader.readAsText(file);
  };

  // Parse email headers for Mail Header Analysis
  const parsedHeaders = useMemo<ParsedEmailHeaders | null>(() => {
    if (!parsedEmail?.headers) return null;

    try {
      return parseEmailHeaders(parsedEmail.headers);
    } catch (error) {
      logger.error('Failed to parse email headers', error);
      return null;
    }
  }, [parsedEmail]);

  // Detect invisible characters in email content
  const invisibleCharsAnalysis = useMemo(() => {
    if (!parsedEmail) return null;

    // Generate cache key from email content
    const cacheKey = AnalysisCache.textHash(
      (parsedEmail.subject || '') + 
      (parsedEmail.textBody || '') + 
      (parsedEmail.htmlBody || '')
    );

    // Check cache first
    if (emailAnalysisCache.has(cacheKey)) {
      return emailAnalysisCache.get<InvisibleCharBulkAnalysisResult[]>(cacheKey);
    }

    // Combine text from subject, plain text body, and extracted HTML text
    const textsToAnalyze: { label: string; text: string }[] = [];

    if (parsedEmail.subject) {
      textsToAnalyze.push({ label: 'Subject', text: parsedEmail.subject });
    }

    if (parsedEmail.textBody) {
      textsToAnalyze.push({ label: 'Plain Text Body', text: parsedEmail.textBody });
    }

    if (parsedEmail.htmlBody) {
      const extractedText = extractTextFromHTML(parsedEmail.htmlBody);
      textsToAnalyze.push({ label: 'HTML Body (extracted text)', text: extractedText });
    }

    // Use unified analyzer for all sections
    const analysis = analyzeBulkInvisibleCharacters(textsToAnalyze, {
      includeSpaces: false,
      includeSecurityAnalysis: true,
      context: 'email'
    });

    // Cache the result
    emailAnalysisCache.set(cacheKey, analysis);
    return analysis;
  }, [parsedEmail]);

  // Extract and analyze URLs from email content
  const urlAnalysis = useMemo(() => {
    if (!parsedEmail) return null;

    // Extract URLs from all text content
    const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;
    const foundUrls = new Set<string>();

    // Search in subject
    if (parsedEmail.subject) {
      const matches = parsedEmail.subject.match(urlRegex);
      if (matches) matches.forEach(url => foundUrls.add(url));
    }

    // Search in text body
    if (parsedEmail.textBody) {
      const matches = parsedEmail.textBody.match(urlRegex);
      if (matches) matches.forEach(url => foundUrls.add(url));
    }

    // Search in HTML body
    if (parsedEmail.htmlBody) {
      // Extract from href attributes
      const hrefRegex = /href=["']([^"']+)["']/gi;
      let match;
      while ((match = hrefRegex.exec(parsedEmail.htmlBody)) !== null) {
        if (match[1].startsWith('http')) {
          // Decode HTML entities (e.g., &amp; to &)
          const decodedUrl = match[1]
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
          foundUrls.add(decodedUrl);
        }
      }
      // Also search in plain text within HTML
      const matches = parsedEmail.htmlBody.match(urlRegex);
      if (matches) matches.forEach(url => foundUrls.add(url));
    }

    // Process and analyze each URL
    const results = Array.from(foundUrls).map(url => {
      let urlToAnalyze = url;
      let isSafeLinkUrl = false;
      let decodedUrl: string | null = null;

      // Check if it's a Microsoft 365 Safe Link
      if (isSafeLinksUrl(url)) {
        isSafeLinkUrl = true;
        const decoded = decodeSafeLinksUrl(url);
        if (decoded) {
          decodedUrl = decoded;
          urlToAnalyze = decoded;
        }
      }

      const analysis = analyzeURL(urlToAnalyze);
      return {
        url,
        isSafeLink: isSafeLinkUrl,
        decodedUrl,
        analysis,
      };
    }).filter(result => result.analysis !== null);

    return results.length > 0 ? results : null;
  }, [parsedEmail]);

  const handleClear = () => {
    setSelectedFile([]);
    setParsedEmail(null);
    setParseError('');
    setHighlightedPosition(null);
  };

  const handlePositionClick = (position: number) => {
    setHighlightedPosition(position);
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedPosition(null);
    }, 3000);
  };

  return (
    <SpaceBetween size="l">
      {/* File Upload Section */}
      <Container
        header={
          <Header
            variant="h2"
            description="Upload an email file in EML format for comprehensive analysis"
            actions={
              selectedFile.length > 0 && (
                <Button onClick={handleClear}>Clear</Button>
              )
            }
          >
            Upload Email File
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FileUpload
            onChange={handleFileChange}
            value={selectedFile}
            i18nStrings={{
              uploadButtonText: () => 'Choose file',
              dropzoneText: () => 'Drop file here to upload',
              removeFileAriaLabel: () => 'Remove file',
              limitShowFewer: 'Show fewer',
              limitShowMore: 'Show more',
              errorIconAriaLabel: 'Error',
            }}
            accept=".eml"
            constraintText="Supported formats: .eml (max 10MB)"
            showFileSize
            showFileThumbnail
            tokenLimit={1}
          />

          {parseError && (
            <Alert type="error" dismissible onDismiss={() => setParseError('')}>
              {parseError}
            </Alert>
          )}

          {!parsedEmail && !parseError && selectedFile.length === 0 && (
            <Alert type="info">
              <SpaceBetween size="xs">
                <Box variant="p">
                  <strong>Privacy Notice:</strong> All email parsing and analysis happens locally in your browser. 
                  No data is sent to any server.
                </Box>
                <Box variant="p">
                  <strong>Supported Format:</strong> EML (Standard email format - RFC 822/2822)
                </Box>
              </SpaceBetween>
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {/* Email Metadata */}
      {parsedEmail && (
        <>
          <Container
            header={
              <Header variant="h2">
                Email Metadata
              </Header>
            }
          >
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">From</Box>
                <Box variant="p">{parsedEmail.from || '(not specified)'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Date</Box>
                <Box variant="p">{parsedEmail.date || '(not specified)'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">To</Box>
                <Box variant="p">{parsedEmail.to.length > 0 ? parsedEmail.to.join(', ') : '(not specified)'}</Box>
              </div>
              {parsedEmail.cc && parsedEmail.cc.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">CC</Box>
                  <Box variant="p">{parsedEmail.cc.join(', ')}</Box>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <Box variant="awsui-key-label">Subject</Box>
                <Box variant="p">{parsedEmail.subject || '(no subject)'}</Box>
              </div>
              {parsedEmail.attachments.length > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <Box variant="awsui-key-label">Attachments ({parsedEmail.attachments.length})</Box>
                  <SpaceBetween size="xs">
                    {parsedEmail.attachments.map((att, index) => (
                      <Box key={index} variant="p">
                        <Badge color="blue">{att.contentType}</Badge> {att.filename} ({(att.size / 1024).toFixed(2)} KB)
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}
            </ColumnLayout>
          </Container>

          {/* Mail Header Analysis */}
          {parsedHeaders && (
            <Container
              header={
                <Header variant="h2">
                  Mail Header Analysis
                </Header>
              }
            >
              <SpaceBetween size="m">
                {/* Authentication Results */}
                {parsedHeaders.authentication && (
                  <div>
                    <Box variant="awsui-key-label" fontSize="heading-m" padding={{ bottom: 's' }}>
                      Authentication Results
                    </Box>
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">SPF</Box>
                        <Badge color={parsedHeaders.authentication.spf?.status === 'pass' ? 'green' : 'red'}>
                          {parsedHeaders.authentication.spf?.status || 'none'}
                        </Badge>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">DKIM</Box>
                        <Badge color={parsedHeaders.authentication.dkim?.status === 'pass' ? 'green' : 'red'}>
                          {parsedHeaders.authentication.dkim?.status || 'none'}
                        </Badge>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">DMARC</Box>
                        <Badge color={parsedHeaders.authentication.dmarc?.status === 'pass' ? 'green' : 'red'}>
                          {parsedHeaders.authentication.dmarc?.status || 'none'}
                        </Badge>
                      </div>
                    </ColumnLayout>
                  </div>
                )}

                {/* Security Issues */}
                {parsedHeaders.securityIssues && parsedHeaders.securityIssues.length > 0 && (
                  <Alert type="warning" header={`${parsedHeaders.securityIssues.length} Security Issue(s) Detected`}>
                    <SpaceBetween size="xs">
                      {parsedHeaders.securityIssues.map((issue, index) => (
                        <Box key={index} variant="p">• {issue}</Box>
                      ))}
                    </SpaceBetween>
                  </Alert>
                )}
              </SpaceBetween>
            </Container>
          )}

          {/* URL Security Analysis */}
          {urlAnalysis && urlAnalysis.length > 0 && (
            <Container
              header={
                <Header variant="h2" description={`${urlAnalysis.length} URL(s) found and analyzed`}>
                  URL Security Analysis
                </Header>
              }
            >
              <SpaceBetween size="m">
                {/* Summary Section */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Total URLs</Box>
                    <Box variant="h3">{urlAnalysis.length}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Safe</Box>
                    <Box variant="h3">
                      <Badge color="green">
                        {urlAnalysis.filter(r => r.analysis?.riskLevel === 'safe').length}
                      </Badge>
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Suspicious</Box>
                    <Box variant="h3">
                      <Badge color="grey">
                        {urlAnalysis.filter(r => r.analysis?.riskLevel === 'suspicious').length}
                      </Badge>
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Dangerous</Box>
                    <Box variant="h3">
                      <Badge color="red">
                        {urlAnalysis.filter(r => r.analysis?.riskLevel === 'dangerous').length}
                      </Badge>
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Microsoft 365 Safe Links</Box>
                    <Box variant="h3">
                      🛡️ {urlAnalysis.filter(r => r.isSafeLink).length}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">With Security Issues</Box>
                    <Box variant="h3">
                      ⚠️ {urlAnalysis.filter(r => r.analysis && r.analysis.issues.length > 0).length}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">With Invisible Characters</Box>
                    <Box variant="h3">
                      👁️ {urlAnalysis.filter(r => r.analysis?.invisibleCharAnalysis && r.analysis.invisibleCharAnalysis.hasInvisibleChars).length}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Average Security Score</Box>
                    <Box variant="h3">
                      {urlAnalysis.length > 0 
                        ? Math.round(urlAnalysis.reduce((sum, r) => sum + (r.analysis?.score || 100), 0) / urlAnalysis.length)
                        : 100}/100
                    </Box>
                  </div>
                </ColumnLayout>

                {/* URLs Grouped by Risk Level */}
                {(() => {
                  // Only include URLs that have analysis results
                  const urlsWithAnalysis = urlAnalysis.filter(r => r.analysis !== null);
                  const dangerousUrls = urlsWithAnalysis.filter(r => r.analysis?.riskLevel === 'dangerous');
                  const suspiciousUrls = urlsWithAnalysis.filter(r => r.analysis?.riskLevel === 'suspicious');
                  const safeUrls = urlsWithAnalysis.filter(r => r.analysis?.riskLevel === 'safe');

                  return (
                    <>
                      {/* Dangerous URLs */}
                      {dangerousUrls.length > 0 && (
                        <ExpandableSection
                          headerText={`🔴 Dangerous URLs (${dangerousUrls.length})`}
                          variant="container"
                          defaultExpanded={true}
                        >
                          <SpaceBetween size="m">
                            {dangerousUrls.map((result, index) => (
                              <ExpandableSection
                                key={`dangerous-${index}`}
                                headerText={result.url}
                                variant="container"
                                defaultExpanded={false}
                              >
                                {result.analysis && (
                                  <SpaceBetween size="m">
                                    <Alert type="error">
                                      This URL has been flagged as <strong>DANGEROUS</strong>. Exercise extreme caution.
                                    </Alert>
                                    {result.isSafeLink && result.decodedUrl && (
                                      <Alert type="info">
                                        <SpaceBetween size="xs">
                                          <Box variant="p"><strong>Microsoft 365 Safe Link Detected</strong></Box>
                                          <Box variant="p">Decoded to: <strong>{result.decodedUrl}</strong></Box>
                                        </SpaceBetween>
                                      </Alert>
                                    )}
                                    <ColumnLayout columns={2} variant="text-grid">
                                      <div>
                                        <Box variant="awsui-key-label">Risk Level</Box>
                                        <Badge color="red">DANGEROUS</Badge>
                                      </div>
                                      <div>
                                        <Box variant="awsui-key-label">Security Score</Box>
                                        <Box variant="p">{result.analysis.score}/100</Box>
                                      </div>
                                    </ColumnLayout>
                                    {result.analysis.issues.length > 0 && (
                                      <div>
                                        <Box variant="awsui-key-label">Security Issues ({result.analysis.issues.length})</Box>
                                        <SpaceBetween size="xs">
                                          {result.analysis.issues.map((issue, issueIndex) => (
                                            <Box key={issueIndex} variant="p">
                                              <Badge color={issue.severity === 'high' ? 'red' : issue.severity === 'medium' ? 'grey' : 'green'}>
                                                {issue.severity.toUpperCase()}
                                              </Badge> <strong>{issue.type}:</strong> {issue.description}
                                            </Box>
                                          ))}
                                        </SpaceBetween>
                                      </div>
                                    )}
                                    {result.analysis.invisibleCharAnalysis && result.analysis.invisibleCharAnalysis.hasInvisibleChars && (
                                      <Alert type="warning">
                                        <Box variant="p"><strong>Found {result.analysis.invisibleCharAnalysis.count} invisible character(s)</strong></Box>
                                      </Alert>
                                    )}
                                  </SpaceBetween>
                                )}
                              </ExpandableSection>
                            ))}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Suspicious URLs */}
                      {suspiciousUrls.length > 0 && (
                        <ExpandableSection
                          headerText={`⚠️ Suspicious URLs (${suspiciousUrls.length})`}
                          variant="container"
                          defaultExpanded={true}
                        >
                          <SpaceBetween size="m">
                            {suspiciousUrls.map((result, index) => (
                              <ExpandableSection
                                key={`suspicious-${index}`}
                                headerText={result.url}
                                variant="container"
                                defaultExpanded={false}
                              >
                                {result.analysis && (
                                  <SpaceBetween size="m">
                                    <Alert type="warning">
                                      This URL has been flagged as <strong>SUSPICIOUS</strong>. Proceed with caution.
                                    </Alert>
                                    {result.isSafeLink && result.decodedUrl && (
                                      <Alert type="info">
                                        <SpaceBetween size="xs">
                                          <Box variant="p"><strong>Microsoft 365 Safe Link Detected</strong></Box>
                                          <Box variant="p">Decoded to: <strong>{result.decodedUrl}</strong></Box>
                                        </SpaceBetween>
                                      </Alert>
                                    )}
                                    <ColumnLayout columns={2} variant="text-grid">
                                      <div>
                                        <Box variant="awsui-key-label">Risk Level</Box>
                                        <Badge color="grey">SUSPICIOUS</Badge>
                                      </div>
                                      <div>
                                        <Box variant="awsui-key-label">Security Score</Box>
                                        <Box variant="p">{result.analysis.score}/100</Box>
                                      </div>
                                    </ColumnLayout>
                                    {result.analysis.issues.length > 0 && (
                                      <div>
                                        <Box variant="awsui-key-label">Security Issues ({result.analysis.issues.length})</Box>
                                        <SpaceBetween size="xs">
                                          {result.analysis.issues.map((issue, issueIndex) => (
                                            <Box key={issueIndex} variant="p">
                                              <Badge color={issue.severity === 'high' ? 'red' : issue.severity === 'medium' ? 'grey' : 'green'}>
                                                {issue.severity.toUpperCase()}
                                              </Badge> <strong>{issue.type}:</strong> {issue.description}
                                            </Box>
                                          ))}
                                        </SpaceBetween>
                                      </div>
                                    )}
                                    {result.analysis.invisibleCharAnalysis && result.analysis.invisibleCharAnalysis.hasInvisibleChars && (
                                      <Alert type="warning">
                                        <Box variant="p"><strong>Found {result.analysis.invisibleCharAnalysis.count} invisible character(s)</strong></Box>
                                      </Alert>
                                    )}
                                  </SpaceBetween>
                                )}
                              </ExpandableSection>
                            ))}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Safe URLs */}
                      {safeUrls.length > 0 && (
                        <ExpandableSection
                          headerText={`✅ Safe URLs (${safeUrls.length})`}
                          variant="container"
                          defaultExpanded={false}
                        >
                          <SpaceBetween size="m">
                            {/* Info about URLs with issues */}
                            {safeUrls.filter(r => r.analysis && r.analysis.issues.length > 0).length > 0 && (
                              <Alert type="info">
                                {safeUrls.filter(r => r.analysis && r.analysis.issues.length > 0).length} of these safe URLs have low-severity security issues that don't significantly impact their safety score.
                              </Alert>
                            )}
                            {safeUrls.map((result, index) => (
                              <ExpandableSection
                                key={`safe-${index}`}
                                headerText={
                                  (() => {
                                    const hasIssues = result.analysis && result.analysis.issues.length > 0;
                                    const baseText = result.isSafeLink && result.decodedUrl
                                      ? `🛡️ ${result.decodedUrl} (Protected by Microsoft 365 Safe Links)` 
                                      : result.url;
                                    return hasIssues ? `⚠️ ${baseText}` : baseText;
                                  })()
                                }
                                variant="container"
                                defaultExpanded={false}
                              >
                                {result.analysis && (
                                  <SpaceBetween size="m">
                                    {/* Safe Link Decoded Info */}
                                    {result.isSafeLink && result.decodedUrl && (
                                      <Alert type="info">
                                        <SpaceBetween size="xs">
                                          <Box variant="p">
                                            <strong>Microsoft 365 Safe Link Detected</strong>
                                          </Box>
                                          <Box variant="p">
                                            This URL was wrapped by Microsoft 365 Safe Links and has been automatically decoded. Security analysis below is for the real destination URL shown above.
                                          </Box>
                                          <div>
                                            <Box variant="awsui-key-label">Original Safe Link (wrapped)</Box>
                                            <Box variant="p">
                                              <code style={{ fontSize: '11px', wordBreak: 'break-all', color: '#666' }}>
                                                {result.url}
                                              </code>
                                            </Box>
                                          </div>
                                        </SpaceBetween>
                                      </Alert>
                                    )}

                                    {/* Risk Assessment */}
                                    <ColumnLayout columns={2} variant="text-grid">
                                      <div>
                                        <Box variant="awsui-key-label">Risk Level</Box>
                                        <Badge color={
                                          result.analysis.riskLevel === 'dangerous' ? 'red' :
                                          result.analysis.riskLevel === 'suspicious' ? 'grey' : 'green'
                                        }>
                                          {result.analysis.riskLevel.toUpperCase()}
                                        </Badge>
                                      </div>
                                      <div>
                                        <Box variant="awsui-key-label">Security Score</Box>
                                        <Box variant="p">{result.analysis.score}/100</Box>
                                      </div>
                                    </ColumnLayout>

                                    {/* Security Issues */}
                                    {result.analysis.issues.length > 0 && (
                                      <div>
                                        <Box variant="awsui-key-label">Security Issues ({result.analysis.issues.length})</Box>
                                        <SpaceBetween size="xs">
                                          {result.analysis.issues.map((issue, issueIndex) => (
                                            <Box key={issueIndex} variant="p">
                                              <Badge color={issue.severity === 'high' ? 'red' : issue.severity === 'medium' ? 'grey' : 'green'}>
                                                {issue.severity.toUpperCase()}
                                              </Badge> <strong>{issue.type}:</strong> {issue.description}
                                            </Box>
                                          ))}
                                        </SpaceBetween>
                                      </div>
                                    )}

                                    {/* Invisible Characters in URL */}
                                    {result.analysis.invisibleCharAnalysis && result.analysis.invisibleCharAnalysis.hasInvisibleChars && (
                                      <Alert type="warning">
                                        <SpaceBetween size="xs">
                                          <Box variant="p">
                                            <strong>Found {result.analysis.invisibleCharAnalysis.count} invisible character(s) in URL</strong>
                                          </Box>
                                          {result.analysis.invisibleCharAnalysis.detectedChars.map((char, charIndex) => (
                                            <Box key={charIndex} variant="p">
                                              • Position {char.position}: {char.character.name} (U+{char.character.code.toString(16).toUpperCase().padStart(4, '0')}) - {char.character.category}
                                            </Box>
                                          ))}
                                        </SpaceBetween>
                                      </Alert>
                                    )}

                                    {result.analysis.issues.length === 0 && (
                                      <Alert type="success">
                                        No obvious security issues detected in this URL
                                      </Alert>
                                    )}
                                  </SpaceBetween>
                                )}
                              </ExpandableSection>
                            ))}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}
                    </>
                  );
                })()}
              </SpaceBetween>
            </Container>
          )}

          {/* Email Body Content */}
          <Container
            header={
              <Header variant="h2">
                Email Body Content
              </Header>
            }
          >
            <Tabs
              activeTabId={bodyTabId}
              onChange={({ detail }) => {
                setBodyTabId(detail.activeTabId);
                setHighlightedPosition(null); // Clear highlight when switching tabs
              }}
              tabs={[
                {
                  id: 'text',
                  label: 'Plain Text',
                  content: (
                    <Box padding={{ vertical: 'm' }}>
                      {parsedEmail.textBody ? (
                        (() => {
                          const textAnalysis = invisibleCharsAnalysis?.find((a: { label: string }) => a.label === 'Plain Text Body');
                          return (
                            <InvisibleCharacterAnalysis
                              text={parsedEmail.textBody}
                              detectedChars={textAnalysis?.analysis.detectedChars || []}
                              title="Plain Text Body Analysis"
                              description="Detailed analysis of invisible characters and security patterns in the plain text body"
                              showLineNumbers={true}
                              showSpacesControl={false}
                              showUnicodeControl={true}
                              showActionButtons={false}
                              allowCopyCharacter={true}
                              securityAnalysis={textAnalysis?.analysis.securityAnalysis || null}
                              onCharacterClick={handlePositionClick}
                              highlightedPosition={highlightedPosition}
                            />
                          );
                        })()
                      ) : (
                        <Box variant="p" color="text-body-secondary">No plain text body found</Box>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'html',
                  label: 'HTML',
                  content: (
                    <Box padding={{ vertical: 'm' }}>
                      {parsedEmail.htmlBody ? (
                        (() => {
                          const htmlAnalysis = invisibleCharsAnalysis?.find((a: { label: string }) => a.label === 'HTML Body (extracted text)');
                          return (
                            <InvisibleCharacterAnalysis
                              text={parsedEmail.htmlBody}
                              detectedChars={htmlAnalysis?.analysis.detectedChars || []}
                              title="HTML Body Analysis"
                              description="Detailed analysis of invisible characters and security patterns in the HTML body"
                              showLineNumbers={true}
                              showSpacesControl={false}
                              showUnicodeControl={true}
                              showActionButtons={false}
                              allowCopyCharacter={true}
                              securityAnalysis={htmlAnalysis?.analysis.securityAnalysis || null}
                              onCharacterClick={handlePositionClick}
                              highlightedPosition={highlightedPosition}
                            />
                          );
                        })()
                      ) : (
                        <Box variant="p" color="text-body-secondary">No HTML body found</Box>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'headers',
                  label: 'Email Headers',
                  content: (
                    <Box padding={{ vertical: 'm' }}>
                      {parsedEmail.headerAnalysis ? (
                        (() => {
                          // Combine all critical headers into a single text string for analysis
                          const headerText = parsedEmail.headerAnalysis.criticalHeaders
                            .map(h => `${h.name}: ${h.decodedValue}`)
                            .join('\n');
                          
                          const detectedChars = detectInvisibleChars(headerText, false);
                          const securityAnalysis = detectedChars.length > 0 
                            ? analyzeSecurityRisks(headerText, detectedChars)
                            : null;

                          return (
                            <InvisibleCharacterAnalysis
                              text={headerText}
                              detectedChars={detectedChars}
                              title="Email Headers Analysis"
                              description="Analysis of invisible characters in critical email headers (From, To, Subject, Reply-To, Return-Path)"
                              showLineNumbers={true}
                              showSpacesControl={false}
                              showUnicodeControl={true}
                              showActionButtons={false}
                              allowCopyCharacter={true}
                              securityAnalysis={securityAnalysis}
                              onCharacterClick={handlePositionClick}
                              highlightedPosition={highlightedPosition}
                            />
                          );
                        })()
                      ) : (
                        <Box variant="p" color="text-body-secondary">No headers found</Box>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          </Container>
        </>
      )}
    </SpaceBetween>
  );
};

